import React, { useState, useEffect, useCallback } from 'react'
import './App.css'
import './Home.css'
import { Link, useLocation } from 'react-router-dom'
import axios from 'axios'
import TopBarProgress from 'react-topbar-progress-indicator'
import { API_URL } from './config'
import { BiSolidLocationPlus } from 'react-icons/bi'
import { BsArrowRight, BsClock, BsSearch } from 'react-icons/bs'
import Header from './layouts/Header'
import IndexedEvents from './IndexedEvents'

const pad = (n) => String(n).padStart(2, '0')

const todayISO = () => {
  const d = new Date()

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const plainText = (html = '', max = 160) => {
  const text = new DOMParser().parseFromString(String(html), 'text/html').body.textContent || ''

  const clean = text.replace(/\s+/g, ' ').trim()

  return clean.length > max ? `${clean.slice(0, max).trimEnd()}…` : clean
}

const posterUrl = (item) =>
  `${API_URL}/apps/kscanner/posters/${item.businessid}/${item.eventid}/${item.poster}`

const hidePoster = (e) => {
  e.currentTarget.style.display = 'none'
}

const Explore = () => {
  const location = useLocation()

  const [province, setProvince] = useState(location.state?.province || '')

  const [currentEvent, setCurrentEvent] = useState([])
  const [provinces, setProvinces] = useState([])
  const [eventCategory, setEventCategory] = useState([])

  const [userData, setUserData] = useState({
    email: '',
    phone: '',
    customername: '',
  })

  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')

  const [loading, setLoading] = useState({
    events: true,
    filters: true,
    error: null,
  })

  const customerid = localStorage.getItem('customerid')
  const accounttype = localStorage.getItem('accounttype')

  const fetchEvents = useCallback((prov) => {
    setLoading((prev) => ({
      ...prev,
      events: true,
      error: null,
    }))

    const queryProv = prov ? `&province=${encodeURIComponent(prov)}` : ''

    const apiUrl = `${API_URL}/apps/kscanner/webservices.php` + `?query=EVENTS${queryProv}`

    fetch(apiUrl)
      .then((res) => res.json())
      .then((result) => {
        setCurrentEvent(Array.isArray(result) ? result : [])

        setLoading((prev) => ({
          ...prev,
          events: false,
        }))
      })
      .catch((error) => {
        setLoading((prev) => ({
          ...prev,
          events: false,
          error,
        }))
      })
  }, [])

  useEffect(() => {
    document.title = province ? `${province} - Karamale` : 'Explore - Karamale'

    if (customerid) {
      axios
        .get(
          `${API_URL}/apps/kscanner/webservices.php?query=CUSTOMERDETAILS&accountid=${customerid}`,
          {
            withCredentials: true,
          },
        )
        .then((response) => {
          setUserData({
            email: response.data.email,
            phone: response.data.phone,
            customername: response.data.customername,
          })
        })
        .catch(() => {})
    }

    fetchEvents(province)

    Promise.all([
      axios.get(`${API_URL}/apps/kscanner/webservices.php?query=PROVINCES`),
      axios.get(`${API_URL}/apps/kscanner/webservices.php?query=EVENTCATEGORY&limit=9`),
    ])
      .then(([provinceResponse, categoryResponse]) => {
        setProvinces(Array.isArray(provinceResponse.data) ? provinceResponse.data : [])

        setEventCategory(Array.isArray(categoryResponse.data) ? categoryResponse.data : [])
      })
      .catch(() => {})
      .finally(() => {
        setLoading((prev) => ({
          ...prev,
          filters: false,
        }))
      })
  }, [province, fetchEvents, customerid])

  const handleProvinceChange = (e) => {
    const provinceId = e.target.value

    if (!provinceId) {
      setProvince('')
      setQuery('')
      setCategory('')
      return
    }

    const selected = provinces.find((item) => String(item.provinceid) === provinceId)

    setProvince(selected ? selected.province : '')
    setQuery('')
    setCategory('')
  }

  const clearFilters = () => {
    setQuery('')
    setCategory('')
  }

  const today = todayISO()

  const visibleEvents = currentEvent.filter(
    (item) => !(item.balance < 3.5 && item.event_day > today),
  )

  const searchTerm = query.trim().toLowerCase()

  const shownEvents = visibleEvents.filter((item) => {
    const matchesCategory = !category || item.eventcategory === category

    const searchableText = `
      ${item.event_name || ''}
      ${item.event_location || ''}
    `.toLowerCase()

    const matchesSearch = !searchTerm || searchableText.includes(searchTerm)

    return matchesCategory && matchesSearch
  })

  const filtering = Boolean(searchTerm || category)

  if (loading.error) {
    return (
      <div className="kx-page">
        <Header />

        <section className="kx-section">
          <div className="kx-wrap">
            <div className="kx-state" role="alert">
              <h3>We couldn't load events</h3>

              <p>Check your connection and try again.</p>

              <button
                type="button"
                className="kx-btn kx-btn--ink"
                onClick={() => fetchEvents(province)}
              >
                Try again
              </button>
            </div>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="kx-page">
      <Header />

      {loading.filters && <TopBarProgress />}

      <main>
        {/* =========================================================
            EXPLORE HEADER / FILTERS
        ========================================================= */}
        <section className="kx-section kx-explore-top">
          <div className="kx-wrap">
            <div className="kx-head kx-explore-head">
              <div className="kx-explore-head__content">
                <h1 className="kx-head__title">Explore events</h1>

                <p className="kx-head__sub">
                  Discover concerts, festivals and experiences{' '}
                  {province ? (
                    <>
                      happening in <strong>{province}</strong>.
                    </>
                  ) : (
                    'happening around you.'
                  )}
                </p>
              </div>

              {!loading.events && (
                <span className="kx-head__count">
                  {shownEvents.length} {shownEvents.length === 1 ? 'event' : 'events'}
                </span>
              )}
            </div>

            {/* Search */}
            <div className="kx-search kx-search--explore">
              <label className="kx-search__field">
                <BsSearch aria-hidden="true" />

                <input
                  type="search"
                  placeholder="Search events or venues"
                  aria-label="Search events or venues"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>

              <label className="kx-search__field">
                <BiSolidLocationPlus aria-hidden="true" />

                <select
                  aria-label="Browse by province"
                  value={provinces.find((p) => p.province === province)?.provinceid || ''}
                  onChange={handleProvinceChange}
                >
                  <option value="">All provinces</option>

                  {provinces.map((p) => (
                    <option key={p.provinceid} value={p.provinceid}>
                      {p.province}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Categories */}
            {eventCategory.length > 0 && (
              <div className="kx-toolbar">
                <div className="kx-chips" role="group" aria-label="Filter by category">
                  <button
                    type="button"
                    className={`kx-chip ${!category ? 'is-active' : ''}`}
                    aria-pressed={!category}
                    onClick={() => setCategory('')}
                  >
                    All
                  </button>

                  {eventCategory.map((item) => (
                    <button
                      key={item.category}
                      type="button"
                      className={`kx-chip ${category === item.category ? 'is-active' : ''}`}
                      aria-pressed={category === item.category}
                      onClick={() => setCategory(item.category)}
                    >
                      {item.category}
                    </button>
                  ))}

                  <Link to="/categories" className="kx-chip kx-chip--dashed">
                    More categories
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* =========================================================
            KARAMALE EVENTS
        ========================================================= */}
        <section className="kx-section kx-section--events">
          <div className="kx-wrap">
            {loading.events && (
              <div className="kx-grid" aria-busy="true" aria-label="Loading events">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div className="kx-skel" key={i}>
                    <div className="kx-skel__img" />

                    <div className="kx-skel__body">
                      <div className="kx-skel__line" />
                      <div className="kx-skel__line" />
                      <div className="kx-skel__line" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading.events && shownEvents.length === 0 && (
              <div className="kx-state">
                <h3>{filtering ? 'No events match your search' : 'No events found'}</h3>

                <p>
                  {filtering
                    ? 'Try a different name, province or category.'
                    : 'New events are added often. Check back soon.'}
                </p>

                {filtering && (
                  <button type="button" className="kx-btn kx-btn--ink" onClick={clearFilters}>
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {!loading.events && shownEvents.length > 0 && (
              <div className="kx-grid">
                {shownEvents.map((item, index) => {
                  const posterUri = posterUrl(item)

                  return (
                    <Link
                      key={item.eventid || index}
                      to={{
                        pathname: `/event/${item.eventid}/tickets`,
                        state: {
                          fromHome: true,
                          eventid: item.eventid,
                          eventDate: item.event_day,
                          fulldate: today,
                          customerid,
                          posterUri,
                          accountid: customerid,
                          accounttype,
                          customername: userData.customername,
                          email: userData.email,
                          phone: userData.phone,
                          venue: item.event_location,
                          description: item.event_des,
                          businessid: item.businessid,
                          poster: item.poster,
                          eventname: item.event_name,
                          eventday: item.eventday,
                          starttime: item.start_time,
                          endtime: item.finish_time,
                          previousPage: 'explore',
                          campaignStatus: 'running',
                          category: item.eventcategory,
                        },
                      }}
                      className="kx-ticket"
                      aria-label={`${item.event_name}, ${item.eventday}`}
                    >
                      <div className="kx-ticket__poster">
                        <img src={posterUri} alt="" loading="lazy" onError={hidePoster} />

                        {item.eventcategory && (
                          <span className="kx-ticket__cat">{item.eventcategory}</span>
                        )}
                      </div>

                      <div className="kx-ticket__body">
                        <h3 className="kx-ticket__title">{item.event_name}</h3>

                        <p className="kx-ticket__place">
                          <BiSolidLocationPlus aria-hidden="true" />

                          <span>{item.event_location}</span>
                        </p>

                        <p className="kx-ticket__desc">{plainText(item.event_des)}</p>
                      </div>

                      <div className="kx-ticket__stub">
                        <div className="kx-ticket__when">
                          <strong>{item.eventday}</strong>

                          {item.start_time && (
                            <span>
                              <BsClock aria-hidden="true" /> {item.start_time}
                            </span>
                          )}
                        </div>

                        <span className="kx-ticket__cta">
                          Get tickets
                          <BsArrowRight aria-hidden="true" />
                        </span>
                      </div>
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        </section>

        {/* =========================================================
            EXTERNAL EVENTS
        ========================================================= */}
        <section className="kx-section kx-section--tight">
          <div className="kx-wrap">
            <IndexedEvents province={province} />
          </div>
        </section>
      </main>
    </div>
  )
}

export default Explore
